import React, { useMemo, useState } from 'react';
import { groupByMonthYear } from '../../../utils/groupByMonthYear';
import { formatDateDisplay, formatMoneyPen, formatQty } from './ventaProduccionDashboardUtils';

const CollapseIcon = ({ collapsed }) =>
  collapsed ? (
    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  ) : (
    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

function sheetDate(row) {
  return row.production_date?.slice?.(0, 10) || row.production_date;
}

/**
 * @param {'simple'|'dashboard'} variant
 */
export default function CuadrosRegistradosMonthTable({
  rows = [],
  onOpenCuadro,
  variant = 'simple',
  showPlantillaColumn = false,
  emptyMessage = 'Aún no hay despachos en este período.',
}) {
  const [collapsed, setCollapsed] = useState({});

  const monthGroups = useMemo(
    () => groupByMonthYear(rows, (row) => sheetDate(row)),
    [rows],
  );

  const toggleMonth = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isCollapsed = (key) => !!collapsed[key];

  const dashExtra = showPlantillaColumn ? 1 : 0;
  const colCount =
    variant === 'dashboard' ? (onOpenCuadro ? 9 + dashExtra : 8 + dashExtra) : 3;

  if (!rows.length) {
    return (
      <div className="mt-3 overflow-x-auto border border-gray-200 rounded-lg bg-white">
        <table className="min-w-full text-sm">
          <tbody>
            <tr>
              <td className="px-4 py-8 text-center text-gray-500">{emptyMessage}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="mt-3 overflow-x-auto border border-gray-200 rounded-lg bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-gray-700">
          <tr>
            <th className="px-4 py-3 font-medium">Fecha</th>
            <th className="px-4 py-3 font-medium">Estado</th>
            {variant === 'dashboard' && showPlantillaColumn && (
              <th className="px-4 py-3 font-medium">Plantilla</th>
            )}
            {variant === 'dashboard' && (
              <>
                <th className="px-4 py-3 font-medium text-right">Filas</th>
                <th className="px-4 py-3 font-medium text-right">Req.</th>
                <th className="px-4 py-3 font-medium text-right">Dif.</th>
                <th className="px-4 py-3 font-medium text-right">Merma</th>
                <th className="px-4 py-3 font-medium">Documentos</th>
                <th className="px-4 py-3 font-medium text-right">Total S/</th>
              </>
            )}
            {onOpenCuadro ? <th className="px-4 py-3 font-medium w-28" /> : null}
          </tr>
        </thead>
        <tbody>
          {monthGroups.map((monthGroup) => {
            const monthKey = monthGroup.key;
            const monthCollapsed = isCollapsed(monthKey);
            const countLabel =
              monthGroup.items.length === 1 ? '1 cuadro' : `${monthGroup.items.length} cuadros`;

            return (
              <React.Fragment key={monthKey}>
                <tr
                  className="bg-gray-100/90 border-y border-gray-200"
                  onClick={() => toggleMonth(monthKey)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleMonth(monthKey)}
                  aria-expanded={!monthCollapsed}
                >
                  <td colSpan={colCount} className="px-4 py-2.5 font-semibold text-gray-800">
                    <div className="flex items-center gap-2">
                      <CollapseIcon collapsed={monthCollapsed} />
                      <span>{monthGroup.label}</span>
                      <span className="text-gray-500 font-normal text-xs">({countLabel})</span>
                    </div>
                  </td>
                </tr>
                {!monthCollapsed &&
                  monthGroup.items.map((row) => {
                    const fecha = sheetDate(row);
                    const c = row.cuadro;
                    return (
                      <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                        <td className="px-4 py-3 pl-10">{formatDateDisplay(fecha)}</td>
                        <td className="px-4 py-3">
                          {row.status === 'confirmed' ? (
                            <span className="text-teal-700 text-xs font-medium">Confirmado</span>
                          ) : (
                            <span className="text-gray-600 text-xs">Borrador</span>
                          )}
                        </td>
                        {variant === 'dashboard' && showPlantillaColumn && (
                          <td className="px-4 py-3 text-gray-700 text-xs">{row.plantilla_name || '—'}</td>
                        )}
                        {variant === 'dashboard' && (
                          <>
                            <td className="px-4 py-3 text-right text-gray-600">{row.rows_count ?? '—'}</td>
                            <td className="px-4 py-3 text-right">
                              {formatQty(row.requerimiento_total ?? c?.requerimiento)}
                            </td>
                            <td className="px-4 py-3 text-right text-amber-800">{formatQty(c?.diferencia)}</td>
                            <td className="px-4 py-3 text-right text-rose-800">
                              {c?.merma_pct != null ? `${Number(c.merma_pct).toFixed(1)}%` : '—'}
                            </td>
                            <td className="px-4 py-3 text-gray-600 text-xs">
                              {(row.documents || []).length === 0
                                ? '—'
                                : (row.documents || [])
                                    .map((d) =>
                                      d.type === 'service_order' ? `OSV ${d.code}` : `VT ${d.code}`,
                                    )
                                    .join(' · ')}
                            </td>
                            <td className="px-4 py-3 text-right font-medium">
                              {row.status === 'confirmed' && row.sales_pen > 0
                                ? formatMoneyPen(row.sales_pen)
                                : '—'}
                            </td>
                          </>
                        )}
                        {onOpenCuadro ? (
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => onOpenCuadro(fecha)}
                              className="text-teal-700 font-medium hover:text-teal-900"
                            >
                              Abrir
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
